import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Dummy table data for outages
const data = [
  { name: 'Jan', outageType1: 50, outageType2: 30, plannedOutages: 20, unplannedOutages: 15, maintenanceOutages: 10 },
  { name: 'Feb', outageType1: 40, outageType2: 20, plannedOutages: 18, unplannedOutages: 12, maintenanceOutages: 8 },
  { name: 'Mar', outageType1: 100, outageType2: 80, plannedOutages: 40, unplannedOutages: 30, maintenanceOutages: 20 },
  { name: 'Apr', outageType1: 60, outageType2: 70, plannedOutages: 35, unplannedOutages: 25, maintenanceOutages: 15 },
  { name: 'May', outageType1: 80, outageType2: 60, plannedOutages: 45, unplannedOutages: 35, maintenanceOutages: 25 },
  { name: 'Jun', outageType1: 90, outageType2: 75, plannedOutages: 50, unplannedOutages: 40, maintenanceOutages: 30 },
  { name: 'Jul', outageType1: 55, outageType2: 65, plannedOutages: 25, unplannedOutages: 18, maintenanceOutages: 12 },
  { name: 'Aug', outageType1: 100, outageType2: 95, plannedOutages: 55, unplannedOutages: 45, maintenanceOutages: 35 },
  { name: 'Sep', outageType1: 30, outageType2: 40, plannedOutages: 15, unplannedOutages: 10, maintenanceOutages: 5 },
  { name: 'Oct', outageType1: 110, outageType2: 100, plannedOutages: 60, unplannedOutages: 50, maintenanceOutages: 40 },
  { name: 'Nov', outageType1: 120, outageType2: 110, plannedOutages: 70, unplannedOutages: 60, maintenanceOutages: 50 },
  { name: 'Dec', outageType1: 130, outageType2: 120, plannedOutages: 80, unplannedOutages: 70, maintenanceOutages: 60 },
];

// Dataset configuration
const initialDatasetsConfig = [
  { id: 'outageType1', label: 'Outage Type 1', color: '#8884d8' },
  { id: 'outageType2', label: 'Outage Type 2', color: '#ff7300' },
  { id: 'plannedOutages', label: 'Planned Outages', color: '#FABE7A' },
  { id: 'unplannedOutages', label: 'Unplanned Outages', color: '#F6866A' },
  { id: 'maintenanceOutages', label: 'Maintenance Outages', color: '#7661E2' },
];

const OutagesStepChart: React.FC = () => {
  // State to manage visibility of each line, defaulting to the first 3 categories being visible
  const [visibility, setVisibility] = useState<any>(
    initialDatasetsConfig.reduce((acc: any, dataset, index) => {
      // Set the first 3 items to true (visible) and the rest to false (hidden)
      acc[dataset.id] = index < 3;
      return acc;
    }, {})
  );

  const toggleVisibility = (id: string) => {
    setVisibility((prev: any) => {
      const newVisibility = { ...prev };
      const selectedCount = Object.values(newVisibility).filter(Boolean).length;

      // If the selected count is 3 or less, toggle the visibility of the clicked item
      if (selectedCount < 3 || newVisibility[id]) {
        newVisibility[id] = !newVisibility[id];
      }

      return newVisibility;
    });
  };

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        padding: '30px',
        borderRadius: '10px',
        position: 'relative',
        boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.1)',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      {/* Heading with clickable labels */}
      <div
        style={{
          fontSize: '16px',
          fontWeight: '500',
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ marginRight: '10px' }}>Outage Categories:</span>

        {initialDatasetsConfig.map((dataset) => (
          <React.Fragment key={dataset.id}>
            <span
              onClick={() => toggleVisibility(dataset.id)}
              style={{
                color: '#a8a8a8',
                fontWeight: visibility[dataset.id] ? '500' : 'normal',
                marginRight: '10px',
                cursor: 'pointer',
              }}
            >
              {dataset.label}
            </span>

            {/* Divider */}
            {dataset !== initialDatasetsConfig[initialDatasetsConfig.length - 1] && (
              <span style={{ marginRight: '10px', color: '#a8a8a8' }}>|</span>
            )}
          </React.Fragment>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 40, right: 30, left: -10, bottom: 30 }}>
          {/* Gradient for Line Stroke */}
          <defs>
            <linearGradient id="colorOutage1" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8884d8" />
              <stop offset="100%" stopColor="#82ca9d" />
            </linearGradient>
            <linearGradient id="colorOutage2" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ff7300" />
              <stop offset="100%" stopColor="#ff6b6b" />
            </linearGradient>
          </defs>

          {/* X Axis */}
          <XAxis dataKey="name" tick={true} axisLine={true} tickSize={10} angle={-45} textAnchor="end" />

          {/* Y Axis */}
          <YAxis tick={true} axisLine={true} />

          {/* Tooltip with improved styling */}
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              fontFamily: 'Poppins, sans-serif',
            }}
            itemStyle={{ fontWeight: 'bold' }}
            formatter={(value: any, name: string) => {
              return (
                <span style={{ color: name === 'plannedOutages' ? '#82ca9d' : name === 'unplannedOutages' ? '#ff6b6b' : '#8884d8' }}>
                  {`${value}`}
                </span>
              );
            }}
            labelFormatter={(value: any) => <span style={{ fontWeight: 'bold', color: '#333' }}>{value}</span>}
            cursor={{ stroke: '#ccc', strokeWidth: 1 }}
          />

          {/* Line for Outage Type 1 */}
          {visibility['outageType1'] && (
            <Line
              type="stepAfter"
              dataKey="outageType1"
              stroke="url(#colorOutage1)"
              strokeWidth={3}
              dot={{
                r: 6,
                fill: '#82ca9d',
                stroke: '#8884d8',
                strokeWidth: 2,
              }}
              activeDot={{
                r: 8,
                fill: '#8884d8',
                stroke: '#82ca9d',
                strokeWidth: 2,
              }}
            />
          )}

          {/* Line for Outage Type 2 */}
          {visibility['outageType2'] && (
            <Line
              type="stepAfter"
              dataKey="outageType2"
              stroke="url(#colorOutage2)"
              strokeWidth={3}
              dot={{
                r: 6,
                fill: '#ff6b6b',
                stroke: '#ff7300',
                strokeWidth: 2,
              }}
              activeDot={{
                r: 8,
                fill: '#ff7300',
                stroke: '#ff6b6b',
                strokeWidth: 2,
              }}
            />
          )}

          {/* Lines for other outage types */}
          {initialDatasetsConfig.map((dataset) => {
            if (dataset.id !== 'outageType1' && dataset.id !== 'outageType2') {
              return (
                visibility[dataset.id] && (
                  <Line
                    key={dataset.id}
                    type="stepAfter"
                    dataKey={dataset.id}
                    stroke={dataset.color}
                    strokeWidth={3}
                    dot={{
                      r: 6,
                      fill: dataset.color,
                      stroke: '#fff',
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 8,
                      fill: '#fff',
                      stroke: dataset.color,
                      strokeWidth: 2,
                    }}
                  />
                )
              );
            }
            return null;
          })}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend Section */}
      <div className="flex gap-8 justify-start mt-8 mb-8 overflow-x-auto">
        {initialDatasetsConfig.map((dataset) => (
          <div key={dataset.id} className="flex flex-col border rounded-lg p-4 bg-[#F6F6F6]">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={visibility[dataset.id]}
                onChange={() => toggleVisibility(dataset.id)}
                className="peer w-4 h-4 accent-violet-500"
              />
              <span className="flex items-center text-sm text-gray-600">
                <span
                  className="inline-block w-4 h-[2px] mr-2"
                  style={{ backgroundColor: dataset.color }}
                />
                {dataset.label}
              </span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OutagesStepChart;
